import "@/App.css";
import { useEffect, useState, lazy, Suspense } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
const Toaster = lazy(() => import("sonner").then((m) => ({ default: m.Toaster })));
import { ThemeProvider } from "@/context/ThemeContext";

import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Services from "@/components/Services";
import TrustBar from "@/components/TrustBar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import { FAQ_ITEMS, SITE } from "@/data/site";

// Routes secondaires chargées à la demande : elles sortent du JS initial des pages publiques.
const LocalPages = () => import("@/components/LocalPages");
const LegalPagesMod = () => import("@/components/LegalPages");
const NewPagesMod = () => import("@/components/NewPages");
const AnnoncesMod = () => import("@/components/Annonces");
const named = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));

const AnnoncesPage = named(AnnoncesMod, "AnnoncesPage");
const AnnonceDetailPage = named(AnnoncesMod, "AnnonceDetailPage");
// Tout ce qui suit le premier écran de l'accueil tient dans un seul lot, chargé dès que le navigateur est libre.
const HomeBelowFold = lazy(() => import("@/components/HomeBelowFold"));

const LocalPage = named(LocalPages, "LocalPage");
const ProjectPage = named(LocalPages, "ProjectPage");
const CityPage = named(LocalPages, "CityPage");
const NotFound = named(LocalPages, "NotFound");
const MentionsLegales = named(LegalPagesMod, "MentionsLegales");
const CGU = named(LegalPagesMod, "CGU");
const CGV = named(LegalPagesMod, "CGV");
const Cookies = named(LegalPagesMod, "Cookies");
const VirtualTips = named(LegalPagesMod, "VirtualTips");
const BlogArticle = named(LegalPagesMod, "BlogArticle");
const PrivacyPolicy = named(LegalPagesMod, "PrivacyPolicy");
const Confidentialite = named(LegalPagesMod, "Confidentialite");
const NosRealisations = named(NewPagesMod, "NosRealisations");
const PortfolioPhotos = named(NewPagesMod, "PortfolioPhotos");
const PortfolioPhotoDetail = named(NewPagesMod, "PortfolioPhotoDetail");
const NosTarifs = named(NewPagesMod, "NosTarifs");
const VisitePage = lazy(() => import("@/components/VisitePage"));
const AdminLogin = named(() => import("@/components/Admin"), "AdminLogin");
const AdminDashboard = named(() => import("@/components/Admin"), "AdminDashboard");
import CookieBanner from "@/components/CookieBanner";
import { MobileStickyBar } from "@/components/MobileStickyBar";

// Le squelette d'attente reprend le fond du thème : pas d'écran blanc entre deux routes.
function RouteFallback() {
  return (
    <div className="min-h-screen" style={{ background: "var(--vp-bg)" }} data-testid="route-loading">
      <div className="pt-40 flex justify-center">
        <span className="w-6 h-6 rounded-full border-2 border-[var(--vp-accent)] border-t-transparent animate-spin" />
      </div>
    </div>
  );
}

// Visitors arriving from outside (Google, direct link) always start at the top of the page,
// even if Google added a "#:~:text=" highlight or a section anchor to the link.
// Un lien direct vers une section (#contact, #faq…) est honoré ; seuls les fragments de surlignage
// ajoutés par Google (#:~:text=) sont retirés pour ouvrir la page tout en haut.
const RAW_HASH = window.location.hash;
const HASH_IS_SECTION = Boolean(RAW_HASH) && !RAW_HASH.startsWith("#:~:");
const ENTRY_EXTERNAL = (() => {
  try {
    return !document.referrer || new URL(document.referrer).origin !== window.location.origin;
  } catch {
    return true;
  }
})();
if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
if (ENTRY_EXTERNAL && RAW_HASH && !HASH_IS_SECTION) {
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
}

function useStartAtTop() {
  useEffect(() => {
    if (!ENTRY_EXTERNAL || HASH_IS_SECTION) return;
    let userMoved = false;
    const stop = () => { userMoved = true; };
    const events = ["touchstart", "wheel", "keydown", "mousedown"];
    events.forEach((e) => window.addEventListener(e, stop, { passive: true, once: true }));
    const toTop = () => { if (!userMoved && window.scrollY > 0) window.scrollTo(0, 0); };
    toTop();
    // Chrome applique un fragment de surlignage Google jusqu'à ~2,5 s après le chargement.
    const timers = [100, 400, 1000, 1800, 2600].map((ms) => setTimeout(toTop, ms));
    window.addEventListener("load", toTop, { once: true });
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("load", toTop);
      events.forEach((e) => window.removeEventListener(e, stop));
    };
  }, []);
}

// In-page anchor links scroll smoothly via JS (native hash jumps skip the reveal animations).
// SPA navigation to another page starts at the top (anchors are handled by Home / useSmoothAnchors).
function ScrollToTopOnRoute() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function useSmoothAnchors() {
  useEffect(() => {
    const onClick = (e) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest("a[href*='#']");
      if (!a || a.target === "_blank") return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.hash) return;
      const el = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      if (!el && url.hash !== "#top") return;
      e.preventDefault();
      // React Router a déjà pris en charge le lien : on ne rajoute pas d'entrée d'historique.
      if (!e.defaultPrevented && window.location.hash !== url.hash) {
        window.history.pushState(null, "", url.hash);
      } else if (window.location.hash !== url.hash) {
        window.history.replaceState(null, "", url.hash);
      }
      // Second passage : les sections chargées en différé peuvent décaler la cible.
      [60, 700].forEach((ms) =>
        setTimeout(() => {
          if (el) el.scrollIntoView({ behavior: "smooth" });
          else window.scrollTo({ top: 0, behavior: "smooth" });
        }, ms)
      );
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
}

function Home() {
  const [faqItems, setFaqItems] = useState(null);
  const [belowFold, setBelowFold] = useState(false);

  // Le bas de page est monté dès que le navigateur est libre (au plus tard 1,2 s) : le premier écran s'affiche sans attendre.
  useEffect(() => {
    const show = () => setBelowFold(true);
    if (window.location.hash) { show(); return; }
    if (window.requestIdleCallback) {
      const id = requestIdleCallback(show, { timeout: 1200 });
      return () => cancelIdleCallback(id);
    }
    const t = setTimeout(show, 300);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/faq`)
      .then((r) => setFaqItems(Array.isArray(r.data) ? r.data : null))
      .catch(() => setFaqItems(null));
  }, []);
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id || id.startsWith(":~:")) return;
    // Les sections du bas arrivent après le premier rendu : on garde la cible en vue
    // pendant une seconde, le temps que les visuels se mettent en place.
    let userMoved = false;
    const stop = () => { userMoved = true; };
    ["wheel", "touchstart", "keydown", "pointerdown"].forEach((e) =>
      window.addEventListener(e, stop, { passive: true, once: true })
    );
    const started = performance.now();
    let found = 0;
    let raf;
    const tick = () => {
      if (userMoved) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView();
        found = found || performance.now();
      }
      const elapsed = performance.now() - started;
      if ((found && performance.now() - found < 1000) || (!found && elapsed < 5000)) {
        raf = requestAnimationFrame(tick);
      }
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      ["wheel", "touchstart", "keydown", "pointerdown"].forEach((e) => window.removeEventListener(e, stop));
    };
  }, []);
  const visibleFaq = faqItems ?? FAQ_ITEMS.map((it) => ({ question: it.q, answer: it.a }));
  const faqSchema = visibleFaq.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: visibleFaq.map((it) => ({
          "@type": "Question",
          name: it.question,
          acceptedAnswer: { "@type": "Answer", text: it.answer },
        })),
      }
    : null;
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Visite virtuelle immobilière 360° Matterport",
    provider: {
      "@type": "LocalBusiness",
      name: "VirtualPeek",
      url: "https://virtualpeek.fr",
      email: SITE.email,
      telephone: "+33641221257",
    },
    areaServed: { "@type": "AdministrativeArea", name: "Hérault" },
    description:
      "Visites virtuelles Matterport dans l'Hérault, depuis Gignac : immobilier résidentiel, commerces, hôtellerie, camping-car, chalet, véhicule. Captation, hébergement et intégration de la visite dans votre annonce.",
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "EUR",
      lowPrice: "149",
      availability: "https://schema.org/InStock",
    },
  };
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "VirtualPeek",
    url: "https://virtualpeek.fr",
    inLanguage: "fr-FR",
  };
  return (
    <div className="App" data-testid="home-page">
      <Seo
        title="Visite virtuelle Matterport dans l'Hérault | VirtualPeek"
        description="Basé à Gignac, VirtualPeek réalise des visites virtuelles Matterport dans l'Hérault pour l'immobilier, les commerces et les hébergements. Exemples et devis."
        path="/"
        keywords="visite virtuelle Hérault, visite virtuelle Matterport, visite virtuelle immobilier, Gignac"
        jsonLd={[websiteSchema, serviceSchema, faqSchema].filter(Boolean)}
      />
      <Header />
      <main>
        <Hero />
        <TrustBar />
        <Services />
        <Suspense fallback={null}>
          {belowFold && <HomeBelowFold faqItems={visibleFaq} />}
        </Suspense>
      </main>
      <Footer />
      <CookieBanner />
      <Suspense fallback={null}>
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#121212",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#fafafa",
          },
        }}
      />
      </Suspense>
    </div>
  );
}

function App() {
  useStartAtTop();
  useSmoothAnchors();
  return (
    <ThemeProvider>
      <BrowserRouter>
        <GoogleAnalytics />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/nos-realisations" element={<NosRealisations />} />
          <Route path="/NosRealisations" element={<NosRealisations />} />
          <Route path="/portfolio" element={<PortfolioPhotos />} />
          <Route path="/Portfolio" element={<PortfolioPhotos />} />
          <Route path="/portfolio/:pid" element={<PortfolioPhotoDetail />} />
          <Route path="/nos-tarifs" element={<NosTarifs />} />
          <Route path="/NosTarifs" element={<NosTarifs />} />
          {["/creation-vcard", "/creation-vcard/:rid", "/goodies-connectes-nfc", "/vcard"].map((p) => (
            <Route key={p} path={p} element={<Navigate to="/" replace />} />
          ))}
          <Route path="/virtualtips" element={<VirtualTips />} />
          <Route path="/virtualtips/:slug" element={<BlogArticle />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/politique-confidentialite" element={<PrivacyPolicy />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          <Route path="/visite-virtuelle-gignac" element={<LocalPage key="gignac" pageKey="gignac" />} />
          <Route path="/visite-virtuelle-clermont-l-herault" element={<LocalPage key="clermont" pageKey="clermont" />} />
          <Route path="/:cityPath" element={<CityPage />} />
          <Route path="/realisations/:slug" element={<ProjectPage />} />
          <Route path="/annonces" element={<AnnoncesPage />} />
          <Route path="/annonces/:slug" element={<AnnonceDetailPage />} />
          <Route path="/visite/:slug/:shortId" element={<VisitePage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <ScrollToTopOnRoute />
        <MobileStickyBar />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
